import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  ShippingFulfillmentPromiseFlatTeamSettingGroupById,
  type ShippingFulfillmentPromiseFlatTeamSettingGroupId,
} from "./groups";

type ShippingFulfillmentPromiseSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: ShippingFulfillmentPromiseSettingInput,
): ShippingFulfillmentPromiseSettingInput {
  return metadata;
}

function shippingFulfillmentPromiseGroup<
  const Group extends ShippingFulfillmentPromiseFlatTeamSettingGroupId,
  const T extends Record<string, ShippingFulfillmentPromiseSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const ShippingFulfillmentPromiseRegistry = {
  ...shippingFulfillmentPromiseGroup(
    "shipping_fulfillment_promise.promise_days",
    {
      "outboundLabels/fulfillmentPromiseDays": setting({
        label: "Fulfillment promise days",
        description:
          "Number of working days it takes to fulfill an order, used to compute the delivery date shown at checkout.",
        synonyms: [
          "fulfillment promise days",
          "promise days",
          "fulfillment days",
          "handling time",
          "processing days",
        ],
      }),
    },
  ),
  ...shippingFulfillmentPromiseGroup(
    "shipping_fulfillment_promise.fulfillment_cutoff",
    {
      "checkoutOptimization/fulfillmentCutoffSchedule": setting({
        label: "Use a custom schedule",
        searchLabels: [
          "Timezone for fulfillment cutoff times",
          "Working days & cutoffs",
          "Cutoff time",
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        description:
          "Custom per-day cutoff times and timezone that determine when an order still ships the same working day.",
        synonyms: [
          "fulfillment cutoff schedule",
          "cutoff times",
          "cutoff schedule",
          "shipping cutoff",
          "order cutoff",
        ],
      }),
    },
  ),
};

type ShippingFulfillmentPromiseFlatTeamSettingKey =
  keyof typeof ShippingFulfillmentPromiseRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ShippingFulfillmentPromiseFlatTeamSettingKeys = Object.keys(
  ShippingFulfillmentPromiseRegistry,
) as ShippingFulfillmentPromiseFlatTeamSettingKey[];

export const ShippingFulfillmentPromiseFlatTeamSettingCatalogDefinitions =
  ShippingFulfillmentPromiseFlatTeamSettingKeys.map((key) => {
    const definition = ShippingFulfillmentPromiseRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group:
          ShippingFulfillmentPromiseFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    ShippingFulfillmentPromiseFlatTeamSettingKey,
    ShippingFulfillmentPromiseFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<ShippingFulfillmentPromiseFlatTeamSettingKey>
  >[];

export const ShippingFulfillmentPromiseFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    ShippingFulfillmentPromiseFlatTeamSettingCatalogDefinitions.map(
      (definition) => [definition.id, definition.metadata],
    ),
  ) as Record<ShippingFulfillmentPromiseFlatTeamSettingKey, SettingMetadata>;
